import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, forwardRef } from 'react';
import omit from 'lodash/fp/omit';
import isNil from 'lodash/fp/isNil';
import isEmpty from 'lodash/fp/isEmpty';
import classNames from 'classnames';
import isFunction from 'lodash/fp/isFunction';
import noop from 'lodash/fp/noop';
import usePrevious from '../../hooks/usePrevious';
export const DEFAULT_TYPE = 'text';
export const SUPPORTED_TYPES = ['text', 'password', 'email'];
const hasValue = (value) => !isEmpty(`${value}`);
const ClearableInput = forwardRef((props, ref) => {
    const { type = DEFAULT_TYPE, defaultValue, value, maxLength, tabIndex = 0, hasError = false, inputRef, autoComplete, onChange = noop, onBlur = noop, onFocus = noop, onClear = noop, onKeyPress = noop, inputClassName = '', className = '', children } = props, remainingProps = __rest(props, ["type", "defaultValue", "value", "maxLength", "tabIndex", "hasError", "inputRef", "autoComplete", "onChange", "onBlur", "onFocus", "onClear", "onKeyPress", "inputClassName", "className", "children"]);
    const initialValue = value || defaultValue || '';
    const [inputValue, setInputValue] = useState(initialValue);
    const [showClear, setShowClear] = useState(hasValue(initialValue));
    const isControlled = !isNil(value);
    // Handles new input value and saves it in the local state.
    // The value stored in the state is used for rendering.
    const changeInternalValue = (newValue = '') => {
        // this is there to prevent that onChange and UNSAFE_componentWillReceiveProps set state at the same time
        if (inputValue === newValue) {
            return;
        }
        setInputValue(newValue);
        setShowClear(hasValue(newValue));
    };
    // important for when used as a controlled component and value changes from the outside.
    const previousValue = usePrevious(value);
    if (previousValue !== value) {
        changeInternalValue(value);
    }
    // only gets triggered on user interaction.
    const handleChange = (event) => {
        const newValue = event.target.value;
        if (isControlled) {
            onChange(newValue, event);
        }
        else {
            changeInternalValue(newValue);
            onChange(newValue, event);
        }
    };
    // Will be triggered on every key press but also when pressing 'Enter' for example
    const handleKeyPress = (event) => {
        onKeyPress(event);
    };
    const clearInputValue = (event) => {
        changeInternalValue('');
        onChange('', event);
        if (onClear) {
            onClear(event);
        }
    };
    const classes = classNames('ClearableInput', 'input-group', hasError && 'has-error', className && className);
    const inputClassNames = classNames('form-control', inputClassName, showClear && 'withClearButton');
    const clearButtonClassNames = classNames('clearButton', !showClear && 'hide');
    const convertedType = type === null || type === void 0 ? void 0 : type.toLowerCase();
    const inputType = SUPPORTED_TYPES.indexOf(convertedType) !== -1 ? convertedType : DEFAULT_TYPE;
    const inputProps = Object.assign(Object.assign({}, omit(['value', 'defaultValue', 'onClear'])(remainingProps)), { className: inputClassNames, autoComplete, type: inputType, value: inputValue, onChange: handleChange, onKeyPress: handleKeyPress, onBlur,
        onFocus,
        maxLength,
        tabIndex, ref: inputRef || ref });
    return (_jsxs("div", Object.assign({ className: classes }, { children: [children && isFunction(children) ? children(inputProps) : _jsx("input", Object.assign({}, inputProps)), _jsx("span", Object.assign({ className: clearButtonClassNames, onClick: clearInputValue }, { children: _jsx("span", { className: 'clearButtonIcon rioglyph rioglyph-remove-sign' }) }))] })));
});
export default ClearableInput;
